import './index.scss';

import { AnchorHTMLAttributes, Component, ComponentType, MouseEvent, ReactNode, createElement } from 'react';

import { BraxImageSource, BraxSize, BraxThemeVariant } from '../../types';
import { BraxDropdown } from '../BraxDropdown';
import { BraxIcon } from '../BraxIcon';
import { BraxListItem, BraxListItemVariant, BraxListProps } from '../BraxList';
import { BraxButton, BraxButtonVariant } from '../BraxButton';

export type BraxCardProps = {
    image: BraxImageSource | BraxImageSource[] | ComponentType | ComponentType[],
    name: ReactNode,
    title?: string,
    link?: string | AnchorHTMLAttributes<HTMLAnchorElement>,
    className?: string,
    size: Extract<BraxSize, 'sm' | 'md' | 'lg'>,
    action?: ReactNode,
    actionVariant?: BraxButtonVariant,
    actionSize?: BraxSize,
    label?: ReactNode,
    index?: ReactNode,
    badge?: ReactNode,
    badgeVariant: Extract<BraxThemeVariant, 'primary' | 'secondary' | 'success' | 'destructive' | 'white'> | 'none',
    items?: BraxListItem[],
    itemsVariant?: BraxListItemVariant,
    hoverable: boolean,
    disabled: boolean,
    overlay: boolean,
    onClick?: (event: MouseEvent<HTMLDivElement>) => void,
    onImageClick?: (event: MouseEvent<HTMLDivElement>) => void,
    onActionClick?: (event: MouseEvent<HTMLButtonElement>) => void
};

type BraxCardState = {
    errors: Record<string, boolean>
};

export class BraxCard extends Component<BraxCardProps, BraxCardState> {

    static defaultProps = {
        size: 'lg',
        badgeVariant: 'primary',
        hoverable: false,
        disabled: false,
        overlay: false
    };

    state = {
        errors: {}
    };

    cardActionClicked = false;

    click(event: MouseEvent<HTMLDivElement>) {
        if (!this.cardActionClicked && !this.props.disabled)
            this.props.onClick?.(event);

        this.cardActionClicked = false;
    }

    actionClick(event: MouseEvent<HTMLButtonElement>) {
        if (this.props.disabled || !this.props.onActionClick)
            return;

        this.cardActionClicked = true;

        this.props.onActionClick(event);
    }

    imageClick(event: MouseEvent<HTMLDivElement>) {
        if (this.props.disabled || !this.props.onImageClick)
            return;

        this.cardActionClicked = true;

        this.props.onImageClick(event);
    }

    toggleDropdown() {
        if (this.props.disabled)
            return;

        this.cardActionClicked = true;
    }

    setError(src: string) {
        this.setState(prevState => ({
            errors: { ...prevState.errors, [src]: true }
        }))
    }

    render() {
        const images = Array.isArray(this.props.image) ? (
            <div className="many-images" onClick={event => this.imageClick(event)}>
                { this.props.image.slice(0, 4).map((image: BraxImageSource | ComponentType, i) => (
                    <div key={i}>
                        <div className="image">
                            { 'src' in image === false
                                ? createElement(image)
                            : !image.src || this.state.errors[image.src]
                                ? <BraxIcon icon="picture" />
                                : <img {...image} title={image.alt} onError={() => this.setError(image.src)} />
                            }
                        </div>
                    </div>
                )) }
            </div>
        ) : (
            <div className="one-image" onClick={event => this.imageClick(event)}>
                <div className="image">
                    { 'src' in this.props.image === false
                        ? createElement(this.props.image)
                    : !this.props.image.src || this.state.errors[this.props.image.src]
                        ? <BraxIcon icon="picture" />
                        : <img {...this.props.image} title={this.props.image.alt} onError={() => this.setError((this.props.image as BraxImageSource).src)} />
                    }
                </div>
            </div>
        );

        const classes = [
            'brax-card',
            this.props.size,
            this.props.action ? 'has-action' : '',
            this.props.hoverable ? 'hoverable' : '',
            this.props.disabled ? 'disabled' : '',
            this.props.overlay ? 'overlay' : '',
            this.props.className || ''
        ];

        return (
            <div className={classes.join(' ')} title={this.props.title} onClick={event => this.click(event)}>
                <div>
                    { this.props.link ? (
                        <a {...(typeof this.props.link === 'string' ? { href: this.props.link } : this.props.link)}>{ images }</a>
                    ) : (
                        images
                    ) }

                    <div>
                        <div>
                            <h1 title={typeof this.props.name === 'string' ? this.props.name : undefined}>{ this.props.name }</h1>

                            { this.props.action ? (
                                <BraxButton variant={this.props.actionVariant} size={this.props.actionSize} disabled={this.props.disabled} onClick={event => this.actionClick(event)}>{ this.props.action }</BraxButton>
                            ) : this.props.label ? (
                                <h2>{ this.props.label }</h2>
                            ) : null }
                        </div>

                        { this.props.items ? (
                            <BraxDropdown items={this.props.items} disabled={this.props.disabled} position="auto-auto" onToggle={() => this.toggleDropdown()}>
                                <BraxIcon icon="three-dots" />
                            </BraxDropdown>
                        ) : null }
                    </div>
                </div>

                { this.props.index ? (
                    <span className="complementary-title brax-card-index">{ this.props.index }</span>
                ) : null }

                { this.props.badge ? (
                    <span className={`brax-card-badge ${this.props.badgeVariant}`}>{ this.props.badge }</span>
                ) : null }

                { this.props.overlay ? (
                    <div className="brax-card-overlay" />
                ) : null }
            </div>
        );
    }

}
